/* Playing a brushed span of the archive.
 *
 * Prefers a proxy over the originals where one exists, and that is the whole
 * product rather than an optimisation. An hour of 24-bit FLAC is ~512 MB and
 * about 49 minutes over a farm uplink; the same hour as a 64 kbit proxy is
 * ~30 MB and arrives in under three. Someone who saw something overnight and
 * wants to hear it now is served by the second and defeated by the first.
 *
 * The proxy is a preview and says so. The hydrophone is calibrated over
 * 20 Hz - 10 kHz, so a 64 kbit proxy discards nothing that could have been
 * reported as measurement -- but it is still a lossy re-encode, and audio a
 * person might quote belongs in a download of the original.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { fmtBytes, type StoredObject } from '../api'
import { stamp } from '../charts'
import { createSegmentPlayer, type PlayerState, type Segment } from '../segments'

function toSegments(objects: StoredObject[], seconds: number): Segment[] {
  return objects
    .filter((o) => o.start_utc)
    .map((o) => ({
      key: o.key,
      startUtc: new Date(o.start_utc!).getTime() / 1000,
      durationS: seconds,
      size: o.size,
      kind: o.kind,
    }))
    .sort((a, b) => a.startUtc - b.startUtc)
}

export function SelectionPlayer({
  missionId, objects, segmentSeconds, t0, t1, onClose,
}: {
  missionId: string
  /** Everything stored in the span, both kinds. */
  objects: StoredObject[]
  segmentSeconds: number
  t0: number
  t1: number
  onClose: () => void
}) {
  const [state, setState] = useState<PlayerState>('idle')
  const [at, setAt] = useState<number>(t0)
  const [bad, setBad] = useState<string[]>([])
  const [useProxy, setUseProxy] = useState(true)

  const proxies = useMemo(
    () => toSegments(objects.filter((o) => o.kind === 'reduced'), segmentSeconds),
    [objects, segmentSeconds])
  const originals = useMemo(
    () => toSegments(objects.filter((o) => o.kind === 'audio'), segmentSeconds),
    [objects, segmentSeconds])

  const playing = useProxy && proxies.length ? proxies : originals
  const bytes = playing.reduce((a, s) => a + (s.size ?? 0), 0)

  const ref = useRef<ReturnType<typeof createSegmentPlayer> | null>(null)
  useEffect(() => {
    if (!playing.length) return
    const p = createSegmentPlayer({
      missionId, segments: playing,
      onTime: setAt, onState: setState,
      onUndecodable: (k) => setBad((b) => (b.includes(k) ? b : [...b, k])),
    })
    ref.current = p
    return () => { p.destroy(); ref.current = null }
    // Rebuilt when the source changes, because swapping the segment list under
    // a running graph would leave the old buffers scheduled against the new
    // clock.
  }, [missionId, playing])

  const span = t1 - t0
  const frac = span > 0 ? Math.min(1, Math.max(0, (at - t0) / span)) : 0

  // Every segment failing to decode is a format this browser will not play,
  // not a broken file. Said once, with the way out, rather than per segment.
  const allBad = playing.length > 0 && bad.length >= playing.length

  return (
    <div className="card selbar">
      <div className="card-body stack">
        <div className="row" style={{ gap: 10, flexWrap: 'wrap' }}>
          <button className="btn primary sm"
            disabled={!playing.length || allBad}
            onClick={() => {
              if (state === 'playing') ref.current?.pause()
              else void ref.current?.play()
            }}>
            {state === 'playing' ? 'Pause' : state === 'paused' ? 'Resume' : 'Play'}
          </button>
          <span className="num faint">{stamp(at)}</span>
          <span className="faint" style={{ fontSize: 13 }}>
            {playing.length} segment{playing.length === 1 ? '' : 's'} · {fmtBytes(bytes)}
          </span>
          <button className="btn ghost sm" style={{ marginLeft: 'auto' }}
            onClick={onClose}>Close</button>
        </div>

        <div className="viewbar-rail" role="progressbar"
          aria-valuemin={t0} aria-valuemax={t1} aria-valuenow={at}
          onClick={(e) => {
            const r = e.currentTarget.getBoundingClientRect()
            ref.current?.seek(t0 + ((e.clientX - r.left) / r.width) * span)
          }}>
          <div className="viewbar-win" style={{ left: 0, width: `${frac * 100}%` }} />
        </div>

        {proxies.length > 0 && originals.length > 0 && (
          <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
            <div className="seg" role="tablist" aria-label="Source">
              <button role="tab" aria-selected={useProxy}
                onClick={() => setUseProxy(true)}>Preview</button>
              <button role="tab" aria-selected={!useProxy}
                onClick={() => setUseProxy(false)}>Original</button>
            </div>
            <span className="faint" style={{ fontSize: 12 }}>
              {useProxy
                ? 'A lossy proxy the node encoded. Fine for finding something;'
                  + ' the hydrophone is only calibrated to 10 kHz, so nothing'
                  + ' measurable is missing — but quote the original.'
                : 'The recording as captured, 24-bit.'}
            </span>
          </div>
        )}

        {!playing.length && (
          <p className="faint" style={{ margin: 0, fontSize: 13 }}>
            Nothing in this span is in storage yet.
          </p>
        )}

        {allBad && (
          <div className="notice bad" style={{ margin: 0 }}>
            This browser will not decode {useProxy ? 'these files' : '24-bit FLAC'}.
            {proxies.length > 0 && !useProxy
              ? ' Switch to Preview, which is encoded for playback.'
              : ' Save the files and open them in something that will.'}
          </div>
        )}

        {bad.length > 0 && !allBad && (
          <p className="faint" style={{ margin: 0, fontSize: 12 }}>
            {bad.length} segment{bad.length === 1 ? '' : 's'} could not be read
            and {bad.length === 1 ? 'plays' : 'play'} as silence. The rest is
            unaffected — a gap is drawn rather than skipped, so what you hear
            stays lined up with the clock.
          </p>
        )}
      </div>
    </div>
  )
}
